import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { BffGetListMeetingsEndpoint } from 'lingo2-api-models';
import {
  IMeetingSession,
  MeetingCancelConfirmation,
  MeetingCancelResponse,
  MeetingCheckoutRequest,
  MeetingCheckoutResponse,
  MeetingDetailsType,
  MeetingJoinResponse,
  MeetingLeaveConfirmation,
  MeetingLeaveResponse,
  MeetingRescheduleConfirmation,
  MeetingRescheduleResponse,
  MeetingTypeEnum,
  type Meeting,
  type MeetingParticipant,
} from 'lingo2-models';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { type ContentSearchableBlock } from '../interfaces';
import { FilesService } from '../lingo2-files/files.service';

export type MeetingsBlockType = ContentSearchableBlock<Meeting, BffGetListMeetingsEndpoint.Body>;

export interface IMeetingsStats {
  /** @see PublishStatusEnum */
  [key: string]: number;
  total: number;
}

@Injectable({
  providedIn: 'root',
})
export class MeetingsService {
  private http = inject(HttpClient);
  public static meetingTypeName(meeting: Meeting): string {
    return (
      {
        [MeetingTypeEnum.single]: 'meeting-type.single',
        [MeetingTypeEnum.group]: 'meeting-type.group',
        [MeetingTypeEnum.webinar]: 'meeting-type.webinar',
        [MeetingTypeEnum.stream]: 'meeting-type.stream',
      }[meeting?.type] || '--'
    );
  }

  public meetingFullTitle(
    meeting: Pick<Meeting, 'title' | 'subject' | 'level' | 'begin_at' | 'end_at' | 'author'>,
  ): string {
    if (!meeting) {
      return null;
    }
    return meeting.title;
  }

  /**
   * Информация о сессии митинга
   */
  public getMeetingSession(id: string): Observable<IMeetingSession> {
    const url = `${environment.content_url}/meeting/${id}/session`;
    return this.http.post<IMeetingSession>(url, {}, { observe: 'response' }).pipe(
      map((response) => response.body),
      // catchError(this.handleError),
    );
  }

  /**
   * Создание быстрого митинга
   */
  public createMeetingQuickSession(values: Partial<Meeting>): Observable<IMeetingSession> {
    const url = `${environment.content_url}/meeting/quick-session`;
    return this.http.post<IMeetingSession>(url, values, { observe: 'response' }).pipe(
      map((response) => response.body),
      // catchError(this.handleError),
    );
  }

  /**
   * Статистика по митингам
   */
  public getStats(user_id?: string): Observable<IMeetingsStats> {
    const url = `${environment.content_url}/meeting/stats/${user_id}`;
    return this.http.get<IMeetingsStats>(url, { observe: 'response' }).pipe(
      map((response) => response.body),
      // catchError(this.handleError),
    );
  }

  public createMeeting(values: Partial<Meeting>): Observable<Meeting> {
    const url = `${environment.content_url}/meeting/`;
    return this.http.post<Meeting>(url, values, { observe: 'response' }).pipe(
      map(this.handleMeetingResponse),
      // catchError(this.handleError),
    );
  }

  /**
   * Проверка доступности времени для митинга
   */
  public checkThatTimeIsAvailableForMeeting(
    from: Date,
    to: Date,
    user_id: string,
    meeting_id?: string,
    details?: MeetingDetailsType[],
  ): Observable<{ result: boolean; meeting: Meeting }> {
    meeting_id = meeting_id || '-';
    let params = new HttpParams();
    if (details && details.length) {
      params = params.set('details', JSON.stringify(details));
    }
    const url = `${environment.content_url}/meeting/${meeting_id}/available-for/${user_id}`;
    return this.http.post(url, { from, to }, { params, observe: 'body' }).pipe(
      map((body: any) => body),
      catchError(async (err: any) => false),
    );
  }

  /** Запрос на удаление митинга */
  public requestMeetingCancel(meeting_id: string): Observable<MeetingCancelConfirmation> {
    const url = `${environment.content_url}/meeting/${meeting_id}/cancel`;
    return this.http
      .post<MeetingCancelConfirmation>(url, { observe: 'body' })
      .pipe(map((response) => new MeetingCancelConfirmation(response)));
  }

  /** Подтверждение удаления митинга */
  public confirmMeetingCancel(meeting_id: string, confirmation_id: string): Observable<MeetingCancelResponse> {
    const url = `${environment.content_url}/meeting/${meeting_id}/cancel/confirm/${confirmation_id}`;
    return this.http
      .post<MeetingCancelResponse>(url, {}, { observe: 'body' })
      .pipe(map((response) => new MeetingCancelResponse(response)));
  }

  /** Запрос на выход из митинга */
  public requestMeetingLeave(meeting_id: string): Observable<MeetingLeaveConfirmation> {
    const url = `${environment.content_url}/meeting/${meeting_id}/leave`;
    return this.http
      .post<MeetingLeaveConfirmation>(url, {}, { observe: 'body' })
      .pipe(map((response) => new MeetingLeaveConfirmation(response)));
  }

  /** Подтверждение выхода из митинга */
  public confirmMeetingLeave(meeting_id: string, confirmation_id: string): Observable<MeetingLeaveResponse> {
    const url = `${environment.content_url}/meeting/${meeting_id}/leave/confirm/${confirmation_id}`;
    return this.http
      .post<MeetingLeaveResponse>(url, {}, { observe: 'body' })
      .pipe(map((response) => new MeetingLeaveResponse(response)));
  }

  /** Запрос на перенос времени митинга */
  public requestMeetingReschedule(meeting_id: string, begin_at: Date): Observable<MeetingRescheduleConfirmation> {
    const url = `${environment.content_url}/meeting/${meeting_id}/reschedule`;
    return this.http
      .post<MeetingRescheduleConfirmation>(url, { begin_at }, { observe: 'body' })
      .pipe(map((response) => new MeetingRescheduleConfirmation(response)));
  }

  /** Подтверждение переноса времени митинга */
  public confirmMeetingReschedule(
    meeting_id: string,
    begin_at: Date,
    confirmation_id: string,
  ): Observable<MeetingRescheduleResponse> {
    const url = `${environment.content_url}/meeting/${meeting_id}/reschedule/confirm/${confirmation_id}`;
    return this.http
      .post<MeetingRescheduleResponse>(url, { begin_at }, { observe: 'body' })
      .pipe(map((response) => new MeetingRescheduleResponse(response)));
  }

  /** Согласие с переносом времени митинга */
  public acceptMeetingReschedule(meeting_id: string): Observable<any> {
    const url = `${environment.content_url}/meeting/${meeting_id}/reschedule/accept`;
    return this.http.post<any>(url, {}, { observe: 'response' }).pipe(map((response) => response.body));
  }

  /** Отказ от переноса времени митинга */
  public declineMeetingReschedule(meeting_id: string): Observable<any> {
    const url = `${environment.content_url}/meeting/${meeting_id}/reschedule/decline`;
    return this.http.post<any>(url, {}, { observe: 'response' }).pipe(map((response) => response.body));
  }

  /** Отмена запроса на перенос времени митинга */
  public withdrawMeetingReschedule(meeting_id: string): Observable<any> {
    const url = `${environment.content_url}/meeting/${meeting_id}/reschedule`;
    return this.http.delete<any>(url, { observe: 'response' }).pipe(map((response) => response.body));
  }

  public updateMeeting(id: string, values: Partial<Meeting>): Observable<Meeting> {
    const url = `${environment.content_url}/meeting/${id}`;
    return this.http.put<Meeting>(url, values, { observe: 'response' }).pipe(map((response) => response.body));
  }

  // добавление участников в митинг
  public inviteUsers(
    id,
    participants: Array<Partial<MeetingParticipant>>,
    details?: MeetingDetailsType[],
  ): Observable<Meeting> {
    const url = `${environment.content_url}/meeting/${id}/participants/invite`;
    let params = new HttpParams();
    if (details && details.length) {
      params = params.set('details', JSON.stringify(details));
    }
    return this.http.post<Meeting>(url, { participants }, { params, observe: 'response' }).pipe(
      map(this.handleMeetingResponse),
      // catchError(this.handleError),
    );
  }

  public joinMeeting(id: string): Observable<MeetingJoinResponse> {
    const url = `${environment.content_url}/meeting/${id}/participants/join`;
    return this.http.post<MeetingJoinResponse>(url, {}, { observe: 'body' }).pipe(
      map((response) => new MeetingJoinResponse(response)),
      // catchError(this.handleError),
    );
  }

  /**
   * Добавить в закладки
   */
  public addBookmark(id: string): Observable<Meeting> {
    const url = `${environment.content_url}/meeting/${id}/bookmark`;
    return this.http.post<Meeting>(url, {}, { observe: 'response' }).pipe(
      map(this.handleMeetingResponse),
      // catchError(this.handleError),
    );
  }

  /**
   * Удалить из закладок
   */
  public removeBookmark(id: string): Observable<Meeting> {
    const url = `${environment.content_url}/meeting/${id}/bookmark`;
    return this.http.delete<Meeting>(url, { observe: 'response' }).pipe(
      map(this.handleMeetingResponse),
      // catchError(this.handleError),
    );
  }

  /**
   * Принять приглашение на митинг
   */
  public acceptMeeting(id: string): Observable<[Meeting, MeetingParticipant]> {
    const url = `${environment.content_url}/meeting/${id}/participants/accept`;
    return this.http.post(url, {}, { observe: 'body' }).pipe(
      map((body: { meeting: Meeting; participant: MeetingParticipant }) => {
        const { meeting, participant } = body;
        return [meeting ? meeting : null, participant ? participant : null];
      }),
    );
  }

  /**
   * Отклонить приглашение на митинг
   */
  public declineMeeting(id: string): Observable<MeetingParticipant> {
    // TODO [Meeting, MeetingParticipant]
    const url = `${environment.content_url}/meeting/${id}/participant
    s/decline`;
    return this.http.post(url, {}, { observe: 'response' }).pipe(map((response: any) => response.participant));
  }

  /** Информация для оплаты митинга */
  public getMeetingCheckoutDetails(request: MeetingCheckoutRequest): Observable<MeetingCheckoutResponse> {
    const url = `${environment.content_url}/meeting/checkout`;
    return this.http
      .post<MeetingCheckoutResponse>(url, request, { observe: 'body' })
      .pipe(map((response) => new MeetingCheckoutResponse(response)));
  }

  public getCoverUrl(meeting: Meeting): string {
    return FilesService.getFileUrlBySize(
      meeting.cover_id ?? meeting.user_service?.cover_id ?? meeting.subject?.cover_id ?? meeting.author?.userpic_id,
    );
  }

  private handleMeetingResponse(response: HttpResponse<Meeting>): Meeting {
    return response.body;
  }
}
